import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
    SliceCaseReducers,
} from '@reduxjs/toolkit';
import { urlUserSettings } from '../../Constants';
import i18n from '../../localization/i18n';
import { IAppReduxStore, IAction, SlicePropsWithData } from '../store';

export enum SidepanelVisibilityEnum {
    expanded = 'expanded',
    collapsed = 'collapsed',
}

/**
 * Timer that limits the number of requests that are sent to the backend when project favourites are changed.
 */
let updateSettingsTimer: number;

/**
 * Initial slice state value.
 */
const initialState: IUserSettingsState = {
    data: {
        language: i18n.language,
        sidepanelVisibility: SidepanelVisibilityEnum.expanded,
    },
    loading: true,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's settings from the backend.
     */
    getSettings: async (_, thunkApi) => {
        try {
            /** Allows this thunk to dispatch actions to other thunks in the redux store. */
            const dispatch = thunkApi.dispatch;

            /** Send GET request to backend */
            const response: Response = await fetch(urlUserSettings);

            /** Get access to the default settings */
            const settings: IUserSettings = {
                ...userSettingsSlice.getInitialState().data,
            };

            /** HTTP 404 means that the user has no settings - therefore create a default settings object. */
            if (response.status === 404) {
                await dispatch(updateSettings(settings));

                return settings;
            }

            /** Get the string content of the response. */
            const jsonObj = await response.json();

            /** Make sure settings JSON is valid. */
            const settingsJson = cleanSettings(jsonObj, settings);

            /** If something in the settings object has changed, send the changes to the backend. */
            if (JSON.stringify(jsonObj) !== JSON.stringify(settingsJson)) {
                await dispatch(updateSettings(settingsJson));
            }

            return settingsJson;
        } catch (e) {
            Promise.reject(e);
        }
    },

    /**
     * Updates the user's settings in the backend.
     */
    updateSettings: async (settings: IUserSettings) => {
        /** Note: Backend only accepts data in form-format! */
        const data: FormData = new FormData();

        data.append('userSettings', JSON.stringify(settings));

        try {
            await fetch(urlUserSettings, { method: 'POST', body: data });

            return settings;
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Updates users application language setting.
     */
    updateLanguage: async (payload: { language: string }, thunkApi) => {
        try {
            /** Allows this thunk to dispatch actions to other thunks in the redux store. */
            const dispatch = thunkApi.dispatch;

            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    language: payload.language,
                };

                dispatch(updateSettings(newSettings));
            }, 2000);
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserSettingsState>) => {
        /**
         * Reducers for the 'getSettings' thunk.
         */
        builder.addCase(getSettings.pending, state => {
            state.loading = true;
        });
        builder.addCase(getSettings.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        });
        builder.addCase(getSettings.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
            state.loading = false;
        });

        /**
         * Reducers for the 'updateSettings' thunk.
         */
        builder.addCase(updateSettings.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateLanguage' thunk.
         */
        builder.addCase(updateLanguage.fulfilled, (state, action) => {
            state.data.language = action.payload;
        });
        builder.addCase(updateLanguage.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });
    },
};

/**
 * Checks to see if two objects have the same properties.
 * @param objA - First object.
 * @param objB - Second object.
 * @returns If the objects have the same properties or not.
 */
const haveSameProperties = <T, X>(objA: T, objB: X): boolean => {
    for (const key in objA) {
        if (!Object.prototype.hasOwnProperty.call(objB, key)) {
            return false;
        }
    }

    for (const key in objB) {
        if (!Object.prototype.hasOwnProperty.call(objA, key)) {
            return false;
        }
    }

    return true;
};

/**
 * Removes properties from a given settings object that do not match the schema of IUserSettings.
 * @param settings - Target settings object.
 * @param original - Reference settings object.
 * @return Updated settings object.
 */
const cleanSettings = (settings: IUserSettings, original: IUserSettings): IUserSettings => {
    Object.keys(settings)
        /** Identify old values that are no longer needed. */
        .filter((x: string): boolean => !Object.prototype.hasOwnProperty.call(original, x))
        /** Remove un-needed properties. */
        .forEach((x: string): boolean => delete settings[x]);

    /** Copy over any missing properties from 'original' with their default values. */
    if (!haveSameProperties(original, settings)) {
        settings = { ...original, ...settings };
    }

    return settings;
};

/**
 * Interface that describes the state object for this redux slice.
 */
export type IUserSettingsState = SlicePropsWithData<IUserSettings> 

/**
 * Interface that describes the settings for a user.
 */
export interface IUserSettings {
    /** User's preferred application language. */
    language: string;

    /** Controls the visibility of the sidepanel. */
    sidepanelVisibility: SidepanelVisibilityEnum;
}

/**
 * Expose thunk functions as exports.
 */
export const getSettings = createAsyncThunk(`${urlUserSettings}/get`, thunks.getSettings);
export const updateSettings = createAsyncThunk(`${urlUserSettings}/update`, thunks.updateSettings);

export const updateLanguage = createAsyncThunk(
    `${urlUserSettings}/language`,
    thunks.updateLanguage,
);

/**
 * Create the redux slice.
 */
export const userSettingsSlice = createSlice<
    IUserSettingsState,
    SliceCaseReducers<IUserSettingsState>
>({
    name: 'userSettings',
    initialState: initialState,
    reducers,
    extraReducers: thunks._reducers,
});

/**
 * Export functions from slice actions.
 */
export const { setShowFilter, setNextArea } = userSettingsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default userSettingsSlice.reducer;